import React, { useState } from 'react'
import { CreditCard, Shield, Zap, Globe, Gift, Star, ArrowRight, Wallet, Loader } from 'lucide-react'
import Header from '../../Components/Header'
import Seo from '../../Components/SeoComponent/Seo'


const ExpenseCard = () => {
    const [selectedCard, setSelectedCard] = useState('platinum')
    const [isHovered, setIsHovered] = useState(false)
    const [isLoading, setIsLoading] = useState(false);

    const cardTypes = {
        platinum: {
            name: 'Platinum Card',
            gradient: 'from-slate-900 via-purple-900 to-slate-900',
            price: '$0',
            features: ['No monthly fees', 'Global acceptance', 'Contactless payments', '24/7 support'],
            bgPattern: 'bg-gradient-to-br'
        },
        gold: {
            name: 'Gold Card',
            gradient: 'from-yellow-600 via-yellow-500 to-orange-600',
            price: '$5/mo',
            features: ['Premium rewards', 'Travel insurance', 'Airport lounge access', 'Concierge service'],
            bgPattern: 'bg-gradient-to-br'
        },
        silver: {
            name: 'Silver Card',
            gradient: 'from-gray-400 via-gray-300 to-gray-500',
            price: '$2/mo',
            features: ['Cashback rewards', 'Mobile banking', 'Free ATM withdrawals', 'Budget tracking'],
            bgPattern: 'bg-gradient-to-br'
        }
    }

    return (
        <>
            <Seo />
            {isLoading && <Loader />}
            <Header heading='Expense Card' />

            <div className="min-h-screen  p-2">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-2" >
                        <h1 className="text-3xl font-bold text-black mb-1 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                            Expense Cards
                        </h1>
                        <p className="text-xl text-blue-600 max-w-xl mx-auto">
                            Experience financial freedom with our cutting-edge Expense card solutions
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-10 items-center">


                        {/* Card Display */}
                        <div className="relative">
                            <div
                                className={`relative w-96 h-56 mx-auto transform transition-all duration-700 ${isHovered ? 'scale-105 rotate-3' : 'hover:scale-105'}`}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                            >
                                <div className={`w-full h-full rounded-2xl bg-blue-600 shadow-2xl relative overflow-hidden`}

                                >
                                    {/* Card Background Pattern */}
                                    <div className="absolute inset-0 opacity-20">
                                        <div className="absolute top-4 right-4 w-32 h-32 rounded-full bg-white/10"></div>
                                        <div className="absolute bottom-4 left-4 w-24 h-24 rounded-full bg-white/5"></div>
                                    </div>

                                    {/* Card Content */}
                                    <div className="relative p-8 h-full flex flex-col justify-between text-white">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <Wallet className="w-8 h-8 mb-2" />
                                                <h3 className="text-lg font-semibold">{cardTypes[selectedCard].name}</h3>
                                            </div>
                                            <div className="flex gap-2">
                                                <div className="w-8 h-6 bg-white/20 rounded"></div>
                                                <div className="w-8 h-6 bg-white/30 rounded"></div>
                                            </div>
                                        </div>

                                        <div>
                                            <div className="mb-4">
                                                <div className="text-2xl font-mono tracking-wider">
                                                    •••• •••• •••• 1234
                                                </div>
                                            </div>

                                            <div className="flex justify-between items-end">
                                                <div>
                                                    <p className="text-xs opacity-70">CARD HOLDER</p>
                                                    <p className="font-semibold">JOHN SMITH</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs opacity-70">EXPIRES</p>
                                                    <p className="font-semibold">12/28</p>
                                                </div>
                                                <CreditCard className="w-10 h-8 opacity-80" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>


                        {/* Card Selection & Features */}
                        <div className="space-y-4">

                            <div>
                                <h2 className="text-2xl font-bold text-blue-600 mb-2">Choose Your Card</h2>
                                <div className="grid grid-cols-3 gap-4">
                                    {Object.entries(cardTypes).map(([key, card]) => (
                                        <button
                                            key={key}
                                            onClick={() => setSelectedCard(key)}
                                            className={`p-4 rounded-xl border-2 transition-all duration-300 ${selectedCard === key
                                                    ? 'border-purple-400 bg-purple-500/20'
                                                    : 'border-gray-600 bg-gray-800/50 hover:border-gray-500'
                                                }`}
                                        >
                                            <div className={`w-full h-3 rounded mb-3 bg-gradient-to-r ${card.gradient}`}></div>
                                            <p className="text-black font-medium text-sm">{card.name}</p>
                                            <p className="text-purple-300 text-xs">{card.price}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-blue-600 mb-4 flex items-center gap-2">
                                    <Star className="w-5 h-5 text-blue-600" />
                                    Card Features
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    {cardTypes[selectedCard].features.map((feature, index) => (
                                        <div key={index} className="flex items-center gap-3 text-green-600">
                                            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                                            <span className="text-sm">{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                                <div className="bg-blue-700 rounded-xl p-2 text-white shadow-lg">
                                    <Shield className="w-8 h-8 text-white mb-2" />
                                    <h4 className="font-semibold mb-1">Secure</h4>
                                    <p className="text-sm">Bank-level encryption</p>
                                </div>

                                <div className="bg-blue-700 rounded-xl p-2 text-white shadow-lg">
                                    <Zap className="w-8 h-8 text-white mb-2" />
                                    <h4 className="font-semibold mb-1">Instant</h4>
                                    <p className="text-sm">Real-time transactions</p>
                                </div>

                                <div className="bg-blue-700 rounded-xl p-2 text-white shadow-lg">
                                    <Globe className="w-8 h-8 text-white mb-2" />
                                    <h4 className="font-semibold mb-1">Global</h4>
                                    <p className="text-sm">Worldwide acceptance</p>
                                </div>

                                <div className="bg-blue-700 rounded-xl p-2 text-white shadow-lg">
                                    <Gift className="w-8 h-8 text-white mb-2" />
                                    <h4 className="font-semibold mb-1">Rewards</h4>
                                    <p className="text-sm">Earn on every purchase</p>
                                </div>
                            </div>


                            <button className=" bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-3 px-3 rounded-2xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-2xl">
                                Comming Soon
                                <ArrowRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>


                    {/* Stats Section */}
                    {/* <div className="mt-2 grid grid-cols-4 gap-2 text-center">
          <div className="bg-blue-700 backdrop-blur-sm rounded-2xl p-6">
            <h3 className="text-3xl font-bold text-white mb-2">2M+</h3>
            <p className="text-gray-400">Active Users</p>
          </div>
          <div className="bg-blue-700 backdrop-blur-sm rounded-2xl p-6">
            <h3 className="text-3xl font-bold text-white mb-2">99.9%</h3>
            <p className="text-gray-400">Uptime</p>
          </div>
          <div className="bg-blue-700 backdrop-blur-sm rounded-2xl p-6">
            <h3 className="text-3xl font-bold text-white mb-2">24/7</h3>
            <p className="text-gray-400">Support</p>
          </div>
          <div className="bg-blue-700 backdrop-blur-sm rounded-2xl p-6">
            <h3 className="text-3xl font-bold text-white mb-2">150+</h3>
            <p className="text-gray-400">Countries</p>
          </div>
        </div> */}


                </div>
                 
<div className="w-full rounded-2xl">
  <div className="flex justify-center">
    <img 
      src="/ExpenseImg.png" 
      className="w-1/2 object-contain" 
      alt="Prepaid"
    />
  </div>
</div>
            </div>
        </>
    )
}

export default ExpenseCard